import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import TopBar from "./components/TopBar";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsAppWidget from "./components/WhatsAppWidget";
import MobileCTA from "./components/MobileCTA";
import AppointmentModal from "./components/AppointmentModal";
import ScrollProgress from "./components/ScrollProgress";
import Home from "./pages/Home";

const ServicePage = lazy(() => import("./pages/ServicePage"));
const BlogList = lazy(() => import("./pages/BlogList"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RevealObserver() {
  const location = useLocation();
  useEffect(() => {
    const timer = setTimeout(() => {
      const revealEls = document.querySelectorAll('.reveal:not(.visible), .reveal-left:not(.visible), .reveal-right:not(.visible)');
      if (!revealEls.length) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
      revealEls.forEach(el => observer.observe(el));
      return () => observer.disconnect();
    }, 80);
    return () => clearTimeout(timer);
  }, [location.pathname]);
  return null;
}

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-8 h-8 border-3 border-teal border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AppContent() {
  const [isAppointmentOpen, setIsAppointmentOpen] = useState(false);
  const openAppointment = () => setIsAppointmentOpen(true);
  const closeAppointment = () => setIsAppointmentOpen(false);

  return (
    <>
      <ScrollProgress />
      <ScrollToTop />
      <RevealObserver />
      <TopBar />
      <Header onBookAppointment={openAppointment} />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home onBookAppointment={openAppointment} />} />
          <Route path="/services/:slug" element={<ServicePage onBookAppointment={openAppointment} />} />
          <Route path="/blogs" element={<BlogList />} />
          <Route path="/blogs/:slug" element={<BlogPage onBookAppointment={openAppointment} />} />
          <Route path="/admin" element={<AdminPanel />} />
        </Routes>
      </Suspense>
      <Footer />
      <WhatsAppWidget />
      <MobileCTA onBookAppointment={openAppointment} />
      <AppointmentModal isOpen={isAppointmentOpen} onClose={closeAppointment} />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
